import { useState, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Check, ExternalLink, ShoppingBag, Download, Link as LinkIcon, ImageOff, Loader2, GripHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, photoUrl } from "@/lib/api";
import DiscogsFields from "./DiscogsFields";
import EtsyFields from "./EtsyFields";
import { startSideBySide, deviceKind, getSplit, setSplit, getAppSide, setAppSide } from "@/lib/sideBySide";
import { useNavigate } from "react-router-dom";
import { Columns2, ArrowLeftRight, Zap } from "lucide-react";

/** Build an "Item specifics" block as human-pastable key: value lines. */
function specificsBlock(specifics) {
  return (specifics || [])
    .filter((s) => s?.key && s?.value)
    .map((s) => `${s.key}: ${s.value}`)
    .join("\n");
}

/** Build a Mercari-friendly description that includes specifics inline. */
function mercariDescription(draft) {
  const parts = [];
  if (draft.description) parts.push(draft.description.trim());
  const specs = specificsBlock(draft.item_specifics);
  if (specs) {
    parts.push("");
    parts.push("Details:");
    parts.push(specs);
  }
  if (draft.condition) {
    parts.push("");
    parts.push(`Condition: ${draft.condition}`);
  }
  return parts.join("\n").trim();
}

function CopyRow({ label, value, hint, testid, size = "default", channel }) {
  const [copied, setCopied] = useState(false);
  const disabled = !value;
  const brand = channel?.color || "#f26222";
  const brandHover = channel?.colorHover || "#d8511a";

  const copy = async () => {
    if (disabled) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-3" data-testid={`quick-paste-row-${testid}`}>
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
          {hint && (
            <Badge variant="outline" className="font-mono text-[9px] uppercase tracking-wider text-slate-500">
              {hint}
            </Badge>
          )}
        </div>
        <Button
          size="sm"
          onClick={copy}
          disabled={disabled}
          className={`h-7 shrink-0 rounded-full px-3 text-xs text-white ${
            copied ? "bg-emerald-600 hover:bg-emerald-700" : ""
          }`}
          style={copied ? undefined : { backgroundColor: brand }}
          onMouseEnter={copied ? undefined : (e) => (e.currentTarget.style.backgroundColor = brandHover)}
          onMouseLeave={copied ? undefined : (e) => (e.currentTarget.style.backgroundColor = brand)}
          data-testid={`quick-paste-copy-${testid}`}
        >
          {copied ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <p
        className={`whitespace-pre-wrap break-words font-mono text-[12px] text-slate-800 ${
          size === "sm" ? "line-clamp-2" : size === "lg" ? "max-h-40 overflow-auto" : ""
        }`}
      >
        {value || <span className="italic text-slate-400">—</span>}
      </p>
    </div>
  );
}

/** Per-channel config. Add new destinations by appending a key here. */
export const CHANNELS = {
  mercari: {
    name: "Mercari",
    sellUrl: "https://www.mercari.com/sell/",
    color: "#f26222",
    colorHover: "#d8511a",
    titleMax: 80,
    note: "Mercari has no public seller API — tap each Copy, then paste into the matching field.",
  },
  poshmark: {
    name: "Poshmark",
    sellUrl: "https://poshmark.com/create-listing",
    color: "#7F1733",
    colorHover: "#60101f",
    titleMax: 50,
    note: "Poshmark caps titles at 50 chars — the Title field below is pre-trimmed.",
  },
  depop: {
    name: "Depop",
    sellUrl: "https://www.depop.com/sell/",
    color: "#ff2300",
    colorHover: "#d41d00",
    titleMax: 65,
    note: "Depop keeps titles short (~65 chars) and relies on hashtags in the description.",
  },
  fb: {
    name: "FB Marketplace",
    sellUrl: "https://www.facebook.com/marketplace/create/item",
    color: "#1877f2",
    colorHover: "#145dbf",
    titleMax: 100,
    note: "FB Marketplace needs you to be logged into Facebook before the sell page loads.",
  },
  vinted: {
    name: "Vinted",
    sellUrl: "https://www.vinted.com/items/new",
    color: "#09B1BA",
    colorHover: "#078d94",
    titleMax: 100,
    note: "Vinted has no seller API — upload the photos first, then copy each field into the form.",
  },
  amazon: {
    name: "Amazon",
    sellUrl: "https://sellercentral.amazon.com/product-search",
    color: "#FF9900",
    colorHover: "#e08600",
    titleMax: 200,
    note: "Amazon lists against existing product pages — find the product, then add your price, condition, note and SKU. Best for books, media, games and branded electronics.",
  },
  craigslist: {
    name: "Craigslist",
    sellUrl: "https://post.craigslist.org/",
    color: "#5A1E91",
    colorHover: "#461775",
    titleMax: 70,
    note: "Craigslist posting titles are short (~70 chars) — pick 'for sale by owner', then paste each field.",
  },
  discogs: {
    name: "Discogs",
    sellUrl: "https://www.discogs.com/sell/list",
    color: "#333333",
    colorHover: "#111111",
    titleMax: 200,
    note: "Discogs is for records, CDs and tapes. Listings attach to a release page (no seller photos) — find the exact pressing, then paste grading, price and comments.",
  },
  etsy: {
    name: "Etsy",
    sellUrl: "https://www.etsy.com/your/shops/me/listing-editor/create",
    color: "#F1641E",
    colorHover: "#D4561A",
    titleMax: 140,
    note: "Etsy titles max 140 chars. Pick Vintage only for items 20+ years old, then add up to 13 tags (tap each to copy).",
  },
  whatnot: {
    name: "Whatnot",
    sellUrl: "https://www.whatnot.com/dashboard",
    color: "#C99700",
    colorHover: "#A67C00",
    titleMax: 80,
    note: "In Whatnot's Seller Hub open Inventory, tap Add product (or Create listing), pick the category, then paste each field.",
  },
};

const CHANNEL_KEYS = Object.keys(CHANNELS);
const SIZE_BRAND_CHANNELS = new Set(["vinted", "poshmark"]);
const PICKUP_CHANNELS = new Set(["craigslist", "fb"]);
const PICKUP_KEY = "snaplist.pickupNote";
const DEFAULT_PICKUP = "Cash only, local pickup. Serious buyers only — message me to arrange a time.";

function specValue(draft, re) {
  const hit = (draft.item_specifics || []).find((s) => s?.value && re.test(s.key || ""));
  return hit ? hit.value : "";
}

function PickupNote({ channel }) {
  const [text, setText] = useState(() => {
    try { return localStorage.getItem(PICKUP_KEY) ?? DEFAULT_PICKUP; } catch { return DEFAULT_PICKUP; }
  });
  const save = (v) => { setText(v); try { localStorage.setItem(PICKUP_KEY, v); } catch { /* ignore */ } };
  return (
    <div className="space-y-1.5" data-testid="quick-paste-pickup">
      <CopyRow label="Pickup / payment line" value={text} hint="editable" testid="pickup" channel={channel} />
      <textarea value={text} onChange={(e) => save(e.target.value.slice(0, 300))} rows={2}
        className="w-full resize-none rounded-lg border border-slate-200 bg-white p-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
        data-testid="quick-paste-pickup-input" aria-label="Edit pickup line" />
      {text !== DEFAULT_PICKUP ? (
        <button type="button" onClick={() => save(DEFAULT_PICKUP)} className="px-1 text-[11px] text-slate-500 underline" data-testid="quick-paste-pickup-reset">
          Reset to default
        </button>
      ) : null}
    </div>
  );
}

const SEARCH_SPEC_KEYS = /^(brand|model|mpn|upc|ean|isbn|manufacturer part number|author|platform)$/i;

/** Brand + model / barcode style terms that match Amazon catalog pages. */
function amazonSearchTerm(draft) {
  const code = (draft.barcode || "").replace(/[^0-9Xx]/g, "");
  if (code.length >= 8) return code;
  const picks = (draft.item_specifics || []).filter((s) => s?.value && SEARCH_SPEC_KEYS.test(s.key || "")).map((s) => s.value);
  const term = picks.length ? picks.join(" ") : (draft.title || "");
  return term.replace(/\s+/g, " ").trim().slice(0, 120);
}

function amazonCondition(raw) {
  const c = (raw || "").toLowerCase();
  if (!c) return "";
  if (c.includes("parts")) return "Not allowed on Amazon (for parts / not working)";
  if (c.includes("open box") || c.includes("excellent") || c.includes("like new")) return "Used - Like New";
  if (c.startsWith("new")) return "New";
  if (c.includes("very good")) return "Used - Very Good";
  if (c.includes("acceptable")) return "Used - Acceptable";
  if (c.includes("good") || c.includes("used")) return "Used - Good";
  return raw;
}

function amazonConditionNote(draft) {
  const first = (draft.description || "").split(/(?<=[.!?])\s+/).slice(0, 2).join(" ");
  return [draft.condition, first].filter(Boolean).join(". ").replace(/\.\./g, ".").slice(0, 1000);
}

function AmazonFields({ draft, channel }) {
  const term = amazonSearchTerm(draft);
  const q = encodeURIComponent(term);
  return (
    <div className="space-y-2" data-testid="quick-paste-amazon">
      <CopyRow label="Product search" value={term} hint={draft.barcode ? "barcode" : "brand · model"} testid="amazon-search" channel={channel} />
      <div className="grid grid-cols-2 gap-2">
        <a href={`https://sellercentral.amazon.com/product-search/search?q=${q}`} target="_blank" rel="noopener noreferrer"
          className="flex h-9 items-center justify-center gap-1 rounded-full border border-[#FF9900] bg-white text-xs font-semibold text-[#b36b00]"
          data-testid="quick-paste-amazon-seller-search">
          <ExternalLink className="h-3 w-3" /> Find in Seller Central
        </a>
        <a href={`https://www.amazon.com/s?k=${q}`} target="_blank" rel="noopener noreferrer"
          className="flex h-9 items-center justify-center gap-1 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700"
          data-testid="quick-paste-amazon-shop-search">
          <ExternalLink className="h-3 w-3" /> Check on Amazon.com
        </a>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <CopyRow label="Price (USD)" value={draft.price ? String(draft.price) : ""} testid="price" channel={channel} />
        <CopyRow label="SKU" value={draft.sku || ""} testid="sku" channel={channel} />
      </div>
      <CopyRow label="Amazon condition" value={amazonCondition(draft.condition)} hint="pick in dropdown" testid="amazon-condition" channel={channel} />
      <CopyRow label="Condition note" value={amazonConditionNote(draft)} hint="max 1000" testid="amazon-condition-note" size="lg" channel={channel} />
      <p className="px-1 text-[11px] leading-relaxed text-slate-500" data-testid="quick-paste-amazon-tip">
        No match? Creating a new product page usually needs a UPC/EAN barcode and may need brand approval. Used clothing, beauty, toys and grocery are restricted for most sellers.
      </p>
    </div>
  );
}
function SplitPicker() {
  const [split, setLocal] = useState(getSplit);
  const [side, setSide] = useState(getAppSide);
  const pick = (v) => { setSplit(v); setLocal(v); };
  const swap = () => { const v = side === "left" ? "right" : "left"; setAppSide(v); setSide(v); };
  return (
    <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500" data-testid="quick-paste-split">
      <button type="button" onClick={swap} data-testid="quick-paste-swap-sides" title="Swap sides"
        className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-semibold text-slate-600 transition-colors hover:bg-slate-100">
        <ArrowLeftRight className="h-3 w-3" />
        <span data-testid="quick-paste-side-label">{side === "left" ? "SnapList left · site right" : "Site left · SnapList right"}</span>
      </button>
      <div className="flex rounded-full bg-slate-100 p-0.5 font-semibold" role="group" aria-label="Screen split">
        {[["half", "Half & half"], ["narrow", "Narrow"]].map(([k, t]) => (
          <button key={k} type="button" onClick={() => pick(k)} data-testid={`quick-paste-split-${k}`}
            className={`rounded-full px-2 py-0.5 transition-colors ${split === k ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}>{t}</button>
        ))}
      </div>
    </div>
  );
}

export const CHANNEL_STORAGE_KEY = "snaplist.quickPasteChannel";

export default function QuickPastePanel({ draft, trigger }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [channelKey, setChannelKey] = useState(() => {
    try {
      const stored = localStorage.getItem(CHANNEL_STORAGE_KEY);
      return stored && CHANNELS[stored] ? stored : "mercari";
    } catch {
      return "mercari";
    }
  });
  const channel = CHANNELS[channelKey];

  const changeChannel = (key) => {
    setChannelKey(key);
    try { localStorage.setItem(CHANNEL_STORAGE_KEY, key); } catch { /* ignore */ }
  };

  const specs = draft?.item_specifics || [];
  const specsBlock = specificsBlock(specs);
  const descForChannel = mercariDescription(draft); // same body shape works everywhere

  const openSellPage = () => {
    window.open(channel.sellUrl, "_blank", "noopener,noreferrer");
  };

  if (!draft) return null;

  const title = (draft.title || "").slice(0, channel.titleMax);
  const rawTitleLen = (draft.title || "").length;

  return (
    <Sheet open={open} onOpenChange={(v) => {
      if (v) { try { const st = localStorage.getItem(CHANNEL_STORAGE_KEY); if (st && CHANNELS[st]) setChannelKey(st); } catch { /* ignore */ } }
      setOpen(v);
    }}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto bg-slate-50 p-0 sm:max-w-md"
        data-testid="quick-paste-sheet"
      >
        {/* Channel-branded header */}
        <div
          className="sticky top-0 z-10 border-b border-slate-200 bg-white px-5 pb-4 pt-6 shadow-sm"
          data-testid="quick-paste-header"
        >
          <SheetHeader>
            <div className="flex items-center gap-2">
              <ShoppingBag className="h-4 w-4" style={{ color: channel.color }} />
              <SheetTitle className="text-base">Quick paste</SheetTitle>
              <Badge
                className="text-white"
                style={{ backgroundColor: channel.color }}
              >
                {channel.name}
              </Badge>
            </div>
            <SheetDescription className="text-xs text-slate-600">
              {channel.note}
            </SheetDescription>
          </SheetHeader>

          {/* Channel picker */}
          <div className="mt-3">
            <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
              Destination
            </Label>
            <Select value={channelKey} onValueChange={changeChannel}>
              <SelectTrigger
                className="mt-1 h-8 text-xs"
                data-testid="quick-paste-channel-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHANNEL_KEYS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`quick-paste-channel-${k}`}>
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ backgroundColor: CHANNELS[k].color }}
                      />
                      {CHANNELS[k].name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={openSellPage}
            size="sm"
            className="mt-3 h-8 w-full rounded-full text-white"
            style={{ backgroundColor: channel.color }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = channel.colorHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = channel.color)}
            data-testid="quick-paste-open-sell"
          >
            <ExternalLink className="mr-1 h-3 w-3" /> Open {channel.name} sell page
          </Button>
          <Button
            onClick={() => startSideBySide(navigate, draft.id, ["amazon", "discogs"].includes(channelKey) ? "other" : channelKey, ["amazon", "discogs"].includes(channelKey) ? null : channel.sellUrl)}
            size="sm"
            variant="outline"
            className="mt-2 h-8 w-full rounded-full border-slate-300 text-xs text-slate-700"
            data-testid="quick-paste-side-by-side"
          >
            <Columns2 className="mr-1 h-3 w-3" /> Side by side with {["amazon", "discogs"].includes(channelKey) ? "any site" : channel.name}
          </Button>
          {deviceKind() === "computer" && <SplitPicker />}
        </div>

        <div className="space-y-2 px-5 py-4">
          {channelKey === "amazon" ? <AmazonFields draft={draft} channel={channel} /> : channelKey === "discogs" ? <DiscogsFields draft={draft} channel={channel} Row={CopyRow} /> : (<>
          {draft.photos && draft.photos.length > 0 && (
            <PhotosSection photos={draft.photos} title={draft.title} channel={channel} draftId={draft.id} savedOrder={draft.paste_photo_order} />
          )}

          <CopyRow
            label="Title"
            value={title}
            hint={`${Math.min(rawTitleLen, channel.titleMax)}/${channel.titleMax}`}
            testid="title"
            channel={channel}
          />

          <div className="grid grid-cols-2 gap-2">
            <CopyRow label="Price (USD)" value={draft.price ? String(draft.price) : ""} testid="price" channel={channel} />
            <CopyRow label="SKU" value={draft.sku || ""} testid="sku" channel={channel} />
          </div>

          {SIZE_BRAND_CHANNELS.has(channelKey) && (
            <div className="grid grid-cols-2 gap-2" data-testid="quick-paste-size-brand">
              <CopyRow label="Size" value={specValue(draft, /size/i)} testid="size" channel={channel} />
              <CopyRow label="Brand" value={specValue(draft, /^brand$/i)} testid="brand" channel={channel} />
            </div>
          )}

          {PICKUP_CHANNELS.has(channelKey) && <PickupNote channel={channel} />}
          {channelKey === "etsy" && <EtsyFields draft={draft} channel={channel} Row={CopyRow} />}

          <div className="grid grid-cols-2 gap-2">
            <CopyRow label="Condition" value={draft.condition || ""} testid="condition" channel={channel} />
            <CopyRow label="Category" value={draft.category || ""} testid="category" hint={draft.category_id ? `#${draft.category_id}` : undefined} channel={channel} />
          </div>

          {/* Specifics — combined block */}
          {specs.length > 0 && (
            <CopyRow
              label={`Specifics · all ${specs.length}`}
              value={specsBlock}
              hint="one block"
              testid="specifics-all"
              size="lg"
              channel={channel}
            />
          )}

          {/* Specifics — per-row */}
          {specs.length > 0 && (
            <div className="mt-2">
              <p className="mb-1.5 px-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                Specifics · per row
              </p>
              <div className="space-y-1.5">
                {specs
                  .filter((s) => s?.key && s?.value)
                  .map((s, i) => (
                    <CopyRow
                      key={`${s.key}-${i}`}
                      label={s.key}
                      value={s.value}
                      testid={`spec-${i}`}
                      size="sm"
                      channel={channel}
                    />
                  ))}
              </div>
            </div>
          )}

          <div className="pt-1">
            <CopyRow
              label="Description (with specifics inline)"
              value={descForChannel}
              hint={`${channel.name}-ready`}
              testid="description"
              size="lg"
              channel={channel}
            />
          </div>
          </>)}
        </div>
      </SheetContent>
    </Sheet>
  );
}


function sanitize(name) {
  return (name || "photo")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()
    .slice(0, 60) || "photo";
}

async function downloadPhoto(url, filename) {
  // Fetch -> blob -> trigger download. Works cross-origin because /api/photos
  // is same-origin via the ingress proxy.
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const blob = await r.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

function DragAllPhotos({ photos, order, title, channel }) {
  const [files, setFiles] = useState(null);
  const key = photos.map((p) => p.id).join(",");
  useEffect(() => {
    let cancelled = false;
    Promise.all(photos.map(async (p) => {
      const r = await fetch(photoUrl(p.id));
      if (!r.ok) return null;
      const blob = await r.blob();
      return [p.id, blob];
    })).then((xs) => { if (!cancelled) setFiles(Object.fromEntries(xs.filter(Boolean))); }).catch(() => { if (!cancelled) setFiles({}); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const ids = (order?.length ? order : photos.map((p) => p.id)).filter((id) => files?.[id]);
  const ready = ids.length > 0;
  const onDragStart = (e) => {
    if (!ready) { e.preventDefault(); toast.info("Photos still getting ready — try again in a moment"); return; }
    const base = sanitize(title);
    e.dataTransfer.items.clear?.();
    ids.forEach((id, i) => {
      const b = files[id];
      e.dataTransfer.items.add(new File([b], `${base}-${String(i + 1).padStart(2, "0")}.jpg`, { type: b.type || "image/jpeg" }));
    });
    e.dataTransfer.effectAllowed = "copy";
  };
  // Chrome usually won't hand photos made on this page to a different window; say so instead of failing silently.
  const onDragEnd = (e) => {
    if (e.dataTransfer.dropEffect !== "none") return;
    toast.info(`${channel?.name || "The site"} didn't take the photos — browsers often block dragging between windows.`, {
      description: "Tap \"Download all\", then pick them all at once in the upload box, or use the SnapList Chrome extension's Add photos button on the site.",
      duration: 9000,
    });
  };
  return (
    <div draggable={ready} onDragStart={onDragStart} onDragEnd={onDragEnd} data-testid="quick-paste-drag-all-photos" data-ready={ready ? "1" : "0"}
      className={`flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-dashed px-3 py-3 text-xs font-semibold transition-colors ${ready ? "cursor-grab active:cursor-grabbing" : "opacity-70"}`}
      style={{ borderColor: channel?.color || "#94a3b8", color: channel?.color || "#334155" }}>
      {ready ? <GripHorizontal className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
      {ready ? `Drag ${order?.length ? `your ${ids.length} picked` : `all ${ids.length}`} photos into ${channel?.name || "the site"}'s upload box` : "Getting photos ready to drag…"}
    </div>
  );
}

// Browsers can't paste several photos into another site; the Chrome extension can, in one click.
function ExtensionPhotosTip({ count, site }) {
  return (
    <a href="/extension" target="_blank" rel="noreferrer" data-testid="quick-paste-extension-photos-tip"
      className="mb-2 flex items-center gap-3 rounded-lg border border-[#0053DA]/30 bg-[#0053DA]/5 px-3 py-2.5 transition-colors hover:bg-[#0053DA]/10">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0053DA] text-white"><Zap className="h-4 w-4" /></span>
      <span className="min-w-0 text-xs leading-snug text-slate-700">
        <span className="block font-semibold text-slate-900">Add all {count} photo{count === 1 ? "" : "s"} in one click</span>
        With the SnapList Chrome extension, open a draft in its side panel, then tap the blue <b>"Fill page + add photos"</b> button in the side panel while the {site || "listing"} page is open. <span className="whitespace-nowrap font-semibold text-[#0053DA]">Set it up →</span>
      </span>
    </a>
  );
}

export function PhotosSection({ photos, title, channel, draftId, savedOrder }) {
  const [bulkRunning, setBulkRunning] = useState(false);
  const [picking, setPicking] = useState(false);
  const [pick, setPickRaw] = useState(() => (savedOrder || []).filter((id) => photos.some((p) => p.id === id)));
  const setPick = (fn) => setPickRaw((xs) => {
    const next = typeof fn === "function" ? fn(xs) : fn;
    if (draftId) api.put(`/paste/photo-order/${draftId}`, { ids: next.length ? next : null }).catch(() => {});
    return next;
  });
  const togglePick = (id) => setPick((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));
  const [copiedAll, setCopiedAll] = useState(false);
  const brand = channel?.color || "#f26222";
  const brandHover = channel?.colorHover || "#d8511a";

  const copyAllUrls = async () => {
    const urls = photos.map((p) => photoUrl(p.id)).join("\n");
    try {
      await navigator.clipboard.writeText(urls);
      setCopiedAll(true);
      toast.success(`${photos.length} photo URL${photos.length === 1 ? "" : "s"} copied`);
      setTimeout(() => setCopiedAll(false), 1200);
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadAll = async () => {
    setBulkRunning(true);
    const base = sanitize(title);
    let ok = 0;
    for (let i = 0; i < photos.length; i++) {
      const p = photos[i];
      try {
        await downloadPhoto(photoUrl(p.id), `${base}-${String(i + 1).padStart(2, "0")}.jpg`);
        ok++;
        // Tiny gap so browsers don't throttle successive downloads
        await new Promise((r) => setTimeout(r, 180));
      } catch {
        /* keep going */
      }
    }
    setBulkRunning(false);
    toast.success(`Downloaded ${ok}/${photos.length} photo${photos.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="quick-paste-photos-section">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
            Photos · {photos.length}
          </span>
          <Badge variant="outline" className="font-mono text-[9px] uppercase tracking-wider text-slate-500">
            drag or right-click → save
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={copyAllUrls}
            className="h-7 rounded-full border-slate-300 px-2 text-[11px] text-slate-700"
            data-testid="quick-paste-copy-all-photo-urls"
          >
            {copiedAll ? <Check className="mr-1 h-3 w-3" /> : <LinkIcon className="mr-1 h-3 w-3" />}
            {copiedAll ? "Copied" : "URLs"}
          </Button>
          <Button
            size="sm"
            onClick={downloadAll}
            disabled={bulkRunning}
            className="h-7 rounded-full px-2 text-[11px] text-white"
            style={{ backgroundColor: brand }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = brandHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = brand)}
            data-testid="quick-paste-download-all-photos"
          >
            <Download className="mr-1 h-3 w-3" />
            {bulkRunning ? "…" : "Download all"}
          </Button>
        </div>
      </div>
      {deviceKind() === "computer" && photos.length > 0 && <ExtensionPhotosTip count={photos.length} site={channel?.name} />}
      {photos.length > 1 && (
        <div className="mb-2 flex items-stretch gap-2">
          <DragAllPhotos photos={photos} order={pick} title={title} channel={channel} />
          <button type="button" onClick={() => setPicking((v) => !v)} data-testid="quick-paste-pick-order"
            className={`shrink-0 rounded-lg border px-2.5 text-[11px] font-semibold transition-colors ${picking ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
            {picking ? "Done" : pick.length ? "Edit order" : "Pick order"}
          </button>
        </div>
      )}
      {picking && (
        <p className="mb-2 flex items-center justify-between gap-2 rounded-md bg-slate-100 px-2.5 py-1.5 text-[11px] text-slate-700" data-testid="quick-paste-pick-hint">
          <span>Tap photos in the order you want them — first tap is the cover. Untapped photos are left out.{draftId ? " Saved for this draft." : ""}</span>
          {pick.length > 0 && <button type="button" onClick={() => setPick([])} className="shrink-0 font-semibold underline" data-testid="quick-paste-pick-reset">Reset</button>}
        </p>
      )}
      <div className="grid grid-cols-3 gap-2">
        {photos.map((p, i) => (
          <div key={p.id} className="relative">
            <PhotoTile photo={p} index={i} title={title} channel={channel} />
            {picking && (
              <button type="button" onClick={() => togglePick(p.id)} data-testid={`quick-paste-pick-${i}`} aria-label={`Pick photo ${i + 1}`}
                className={`absolute inset-0 flex items-center justify-center rounded-md transition-colors ${pick.includes(p.id) ? "bg-black/20 ring-2" : "bg-white/50 hover:bg-white/30"}`}
                style={pick.includes(p.id) ? { "--tw-ring-color": channel?.color || "#0f172a" } : undefined}>
                {pick.includes(p.id) && (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full font-mono text-sm font-bold text-white shadow" style={{ backgroundColor: channel?.color || "#0f172a" }}>
                    {pick.indexOf(p.id) + 1}
                  </span>
                )}
              </button>
            )}
            {!picking && pick.includes(p.id) && (
              <span className="pointer-events-none absolute bottom-1 left-1 rounded-full px-1.5 font-mono text-[10px] font-bold text-white" style={{ backgroundColor: channel?.color || "#0f172a" }}>
                #{pick.indexOf(p.id) + 1}
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="mt-2 text-[10px] leading-snug text-slate-500">
        Tip: drag a tile straight into {channel?.name || "the"} upload drop zone — the <span className="font-mono font-semibold text-emerald-700">DRAG</span> badge
        means the photo is ready. If the site won't take a drag from another window, tap Download all and pick them all at once, or use the SnapList extension's Add photos button on the site.
      </p>
    </div>
  );
}

function PhotoTile({ photo, index, title, channel }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [file, setFile] = useState(null);           // File ready for cross-tab drag
  const [prefetching, setPrefetching] = useState(true);
  const url = photoUrl(photo.id);
  const thumbUrl = photoUrl(photo.id, "card");  // 320px preview for display only
  const filename = `${sanitize(title)}-${String(index + 1).padStart(2, "0")}.jpg`;
  const brand = channel?.color || "#f26222";
  const brandHover = channel?.colorHover || "#d8511a";
  const channelName = channel?.name || "the";

  // Pre-fetch the photo as a File so we can hand a real file to Mercari's
  // drop zone during the synchronous `dragstart` event (fetch can't run
  // inside dragstart). ~1-3MB per photo; acceptable for a sheet that only
  // opens on demand.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        if (cancelled) return;
        setFile(new File([blob], filename, { type: blob.type || "image/jpeg" }));
      } catch {
        /* keep file null — drag will fall back to the default <img> drag */
      } finally {
        if (!cancelled) setPrefetching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url, filename]);

  const copyUrl = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Photo URL copied");
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error("Copy failed");
    }
  };

  const download = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDownloading(true);
    try {
      await downloadPhoto(url, filename);
      toast.success(`Saved ${filename}`);
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const onDragStart = (e) => {
    if (!file) {
      e.preventDefault();
      if (prefetching) toast.info("Photo still preparing — try again in a moment");
      else toast.error("Couldn't ready the photo for drag — use Download instead");
      return;
    }
    try {
      // Chrome/Edge: adding a File to DataTransferItemList lets the drop
      // target (any marketplace's uploader) receive it in e.dataTransfer.files.
      if (e.dataTransfer.items) {
        e.dataTransfer.items.clear?.();
        e.dataTransfer.items.add(file);
      }
      // DownloadURL fallback for drops to the desktop (Chromium).
      e.dataTransfer.setData(
        "DownloadURL",
        `${file.type || "image/jpeg"}:${filename}:${url}`
      );
      e.dataTransfer.effectAllowed = "copy";
    } catch {
      /* leave default behaviour */
    }
  };

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      draggable
      onDragStart={onDragStart}
      className={`group relative block overflow-hidden rounded-md border bg-slate-50 transition ${
        file ? "cursor-grab active:cursor-grabbing" : ""
      }`}
      style={file ? { borderColor: "#e2e8f0" } : undefined}
      onMouseEnter={file ? (e) => (e.currentTarget.style.borderColor = brand) : undefined}
      onMouseLeave={file ? (e) => (e.currentTarget.style.borderColor = "#e2e8f0") : undefined}
      title={file ? `Drag into ${channelName} uploader · click to open full size · right-click to save` : "Preparing drag file…"}
      data-testid={`quick-paste-photo-${index}`}
    >
      <div className="aspect-square overflow-hidden">
        {photo.id ? (
          <img
            src={thumbUrl}
            alt={`${title || "photo"} ${index + 1}`}
            className="pointer-events-none h-full w-full object-cover transition-transform group-hover:scale-105"
            draggable={false}
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-slate-300">
            <ImageOff className="h-5 w-5" />
          </div>
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          type="button"
          onClick={copyUrl}
          className="rounded-full bg-white/90 p-1 text-slate-800 hover:bg-white"
          title="Copy URL"
          data-testid={`quick-paste-photo-copy-${index}`}
        >
          {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <LinkIcon className="h-3 w-3" />}
        </button>
        <button
          type="button"
          onClick={download}
          disabled={downloading}
          className="rounded-full p-1 text-white disabled:opacity-60"
          style={{ backgroundColor: brand }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = brandHover)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = brand)}
          title="Download"
          data-testid={`quick-paste-photo-download-${index}`}
        >
          <Download className="h-3 w-3" />
        </button>
      </div>
      <span className="absolute left-1 top-1 rounded bg-black/60 px-1 font-mono text-[9px] font-bold text-white">
        {index + 1}
      </span>
      {/* Drag-ready indicator */}
      <span
        className={`absolute right-1 top-1 inline-flex items-center gap-0.5 rounded px-1 py-0.5 font-mono text-[9px] font-bold ${
          file
            ? "bg-emerald-600/90 text-white"
            : "bg-slate-900/70 text-white"
        }`}
        title={file ? "Ready to drag" : "Preparing drag file"}
        data-testid={`quick-paste-photo-dragstate-${index}`}
      >
        {file ? <GripHorizontal className="h-2.5 w-2.5" /> : <Loader2 className="h-2.5 w-2.5 animate-spin" />}
        {file ? "DRAG" : "…"}
      </span>
    </a>
  );
}
